import { Injectable } from '@angular/core';

import { Oauth2RessourceService } from '@app/oauth2/oauth2-resources.service';

import { ConfigurationService, Oauth2Service } from '@common';
import { Observable, forkJoin as observableForkJoin, from as observableFrom, of as observableOf } from 'rxjs';
import { catchError, map, mergeMap, share, tap } from 'rxjs/operators';

import { DateUniversal } from '../../base/classes/date-universal.class';
import { REFCLI_TYPE } from '../../baseBtl/context/contract.interface';
import { Promotion } from '../../checkout/cart/promotions/promotion.class';
import { OrdersInterface } from '../../sales/orders.interface';
import { ContextStorageService } from '../context-storage.service';
import { ContextInterface } from '../context.interface';
import { ContextService } from '../context.service';
import { ContractInterface } from '../contract.interface';

@Injectable({
    providedIn: 'root',
})
export class CustomerContextService implements ContextInterface {
    public keySerialize: string = 'CustomerContextService';
    public prefix: string = '';
    public firstname: string = '';
    public lastname: string = '';
    public identity_id: string = null;

    public idPersonne: number = null;
    public type: string[] = [];
    public contracts: ContractInterface[] = [];
    public statut: string[] = [];
    private orders: OrdersInterface = null;
    public defaultContractId: string;
    public isConvergenceType: boolean = false;

    constructor(
        private oauth2Ressource: Oauth2RessourceService,
        private contextStorageService: ContextStorageService,
        private oauth2Service: Oauth2Service,
        private configService: ConfigurationService,
    ) {}

    public create(personneId?: number): Observable<ContextInterface> {
        // eslint-disable-next-line max-len
        const instance: CustomerContextService = new CustomerContextService(
            this.oauth2Ressource,
            this.contextStorageService,
            this.oauth2Service,
            this.configService,
        );
        if (personneId === 0) {
            return instance.configProspect();
        }
        if (this.oauth2Service.isConnected || personneId) {
            const personId: number = personneId || this.oauth2Service.getPersonId();
            if (personId) {
                return this.config(personId);
            } else {
                return instance.configProspect();
            }
        }

        return instance.configProspect();
    }

    public updateContext(contextService: ContextService): Observable<ContextService> {
        return observableFrom([contextService]);
    }

    public getOrders(): Observable<OrdersInterface> {
        if (!this.idPersonne || this.orders) {
            return observableOf(this.orders);
        }

        return this.oauth2Ressource
            .personnes(this.idPersonne)
            .commandes()
            .get()
            .pipe(
                tap((data) => (this.orders = data)),
                map(() => this.orders),
            );
    }

    public config(idPerson: number): Observable<ContextInterface> {
        this.idPersonne = idPerson;
        return observableForkJoin([
            this.oauth2Ressource.personnes(this.idPersonne).get(),
            this.oauth2Ressource.personnes(this.idPersonne).contratsSignes().get(),
        ]).pipe(
            mergeMap((responses: any) => {
                if (responses[0].representantLegal) {
                    this.statut.push(REFCLI_TYPE.ENTREPRISE);
                    this.firstname = responses[0].representantLegal.prenom;
                    this.lastname = responses[0].representantLegal.nom;
                } else {
                    this.firstname = responses[0].prenom;
                    this.lastname = responses[0].nom;
                    this.statut.push(REFCLI_TYPE.INDIVIDU);
                }

                this.contracts = responses[1].items.filter((item: any) => item.statut === 'ACTIF');
                this.contracts = this.contracts.map((item: any) => item as ContractInterface);

                if (this.contracts.length) {
                    this.type.push('customer');
                    this.isConvergenceType = Promotion.hasContractType(null, this.contracts);
                }

                const LIGNES = {
                    FNB: {
                        date: null,
                        id: null,
                    },
                    FAI: {
                        date: null,
                        id: null,
                    },
                    FB: {
                        date: null,
                        id: null,
                    },
                };

                if (!responses[1]._links.contratsSignes) {
                    this.defaultContractId = null;
                }

                for (const contract of this.contracts) {
                    if (
                        (contract.ligneMarche && LIGNES[contract.ligneMarche].date === null) ||
                        LIGNES[contract.ligneMarche].date > new DateUniversal(contract.abonnement.dateActivation)
                    ) {
                        LIGNES[contract.ligneMarche].date = new DateUniversal(contract.abonnement.dateActivation);
                        LIGNES[contract.ligneMarche].id = contract.id;
                    }
                }

                for (const key in LIGNES) {
                    if (LIGNES[key].id) {
                        this.defaultContractId = LIGNES[key].id;
                    }
                }

                return observableFrom([this]);
            }),
            share(),
            catchError(() => observableFrom([this])),
        );
    }

    public configProspect(): Observable<ContextInterface> {
        this.type.push('prospect');
        return observableFrom([this]);
    }

    public serialize(): { [index: string]: any } {
        return {
            firstname: this.firstname,
            lastname: this.lastname,
        };
    }

    public isClientLoggedIn(): boolean {
        return this.oauth2Service.loggedPicasso();
    }

    public isFaiPremium(): boolean {
        return this.contracts.some((item) => {
            if (
                item.statut === 'ACTIF' &&
                item.abonnement &&
                item.abonnement.offrePremium &&
                (item.ligneMarche === 'FAI' || (item.ligneMarche === 'FNB' && item.abonnement.offreTokyo))
            ) {
                return true;
            }
        });
    }

    public isPremiumForPromo(): boolean {
        const data = this.contextStorageService.getItem(this.contextStorageService.KEY.PROMO_CONTEXT);
        if (!data) {
            return false;
        } else {
            return data.niveauGamme === 'PREMIUM';
        }
    }

    public isStandardForPromo(): boolean {
        const data = this.contextStorageService.getItem(this.contextStorageService.KEY.PROMO_CONTEXT);
        if (!data) {
            return false;
        } else {
            return data.niveauGamme === 'STANDARD';
        }
    }

    public isEnterprise(): boolean {
        return this.statut.includes('ENTREPRISE');
    }

    public isIndividu(): boolean {
        return this.statut.includes(REFCLI_TYPE.INDIVIDU);
    }

    public getAddresses(): Observable<any> {
        if (!this.idPersonne) {
            return observableOf([]);
        }
        return this.oauth2Ressource
            .personnes(this.idPersonne)
            .coordonnees()
            .get()
            .pipe(map((res) => res.adressesPostales || []));
    }
}
